import React, { useEffect, useState } from 'react';
import { reportAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const StatCard = ({ label, value, color, icon, delay = 0 }) => (
  <div className="stat-card" style={{ animationDelay: `${delay}ms`, animation: 'fadeIn 0.4s ease forwards', borderTop: `2px solid ${color}` }}>
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={{ color }}>{value ?? '—'}</div>
    <div className="stat-icon" style={{ fontSize: 32 }}>{icon}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportAPI.getDashboard()
      .then(res => setStats(res.data.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <span className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  const cartonData = stats ? [
    { name: 'Pending Sort', value: stats.pendingSorting || 0, color: '#f59e0b' },
    { name: 'In Transit', value: stats.inTransit || 0, color: '#8b5cf6' },
    { name: 'Arrived BD', value: stats.arrivedBD || 0, color: '#06b6d4' },
    { name: 'Delivered', value: stats.deliveredCartons || 0, color: '#10b981' },
  ] : [];

  const shipmentData = stats ? [
    { name: 'Active', value: stats.activeShipments || 0 },
    { name: 'Completed', value: stats.completedShipments || 0 },
    { name: 'Total', value: stats.totalShipments || 0 },
  ] : [];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Real-time overview of your cargo operations</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard label="Total Cartons" value={stats?.totalCartons} color="var(--accent)" icon="▣" delay={0} />
        <StatCard label="Pending Sorting" value={stats?.pendingSorting} color="var(--warning)" icon="◈" delay={60} />
        <StatCard label="In Transit" value={stats?.inTransit} color="#8b5cf6" icon="⬡" delay={120} />
        <StatCard label="Arrived BD" value={stats?.arrivedBD} color="var(--accent-2)" icon="◎" delay={180} />
        <StatCard label="Delivered" value={stats?.deliveredCartons} color="var(--success)" icon="✓" delay={240} />
        <StatCard label="Total Shipments" value={stats?.totalShipments} color="var(--accent)" icon="▦" delay={300} />
        <StatCard label="Active Shipments" value={stats?.activeShipments} color="#f59e0b" icon="⊹" delay={360} />
        <StatCard label="Completed" value={stats?.completedShipments} color="var(--success)" icon="◉" delay={420} />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 8 }}>
        {/* Bar chart */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 20 }}>Carton Status Overview</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={cartonData} barSize={32}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13 }}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              {cartonData.map((entry, i) => (
                <Bar key={i} dataKey="value" fill={entry.color} radius={[4,4,0,0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 20 }}>Shipment Distribution</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={cartonData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                  {cartonData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ minWidth: 120 }}>
              {cartonData.map((entry, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{entry.name}</span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: entry.color, marginLeft: 'auto' }}>{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card" style={{ marginTop: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 16 }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: '+ Receive Carton', path: '/warehouse', color: 'btn-primary' },
            { label: '+ New Shipment', path: '/shipments', color: 'btn-primary' },
            { label: '+ Add Customer', path: '/customers', color: 'btn-secondary' },
            { label: '↗ Track Barcode', path: '/tracking', color: 'btn-secondary' },
          ].map(a => (
            <a key={a.label} href={a.path} className={`btn ${a.color}`}>{a.label}</a>
          ))}
        </div>
      </div>
    </div>
  );
}
